import React, { useEffect, useState } from "react";
import api from "../utils/api.js";
import { URLS } from "../utils/urls.js";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { TrendingUp, Users, MapPin, Smile } from "lucide-react";
import { useTopLoader } from "../context/TopLoaderContext";

const COLORS = ["#db8654", "#f3b183", "#4F46E5", "#10B981", "#EF4444", "#F59E0B", "#6366F1", "#EC4899"];

const AnalyticsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { start, stop } = useTopLoader();

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        start();
        setLoading(true);
        // Using existing stats endpoint as base
        const res = await api.get(URLS.STATS);
        setStats(res.data);
      } catch (err) {
        console.error("Failed to fetch analytics:", err);
      } finally {
        setLoading(false);
        stop();
      }
    };
    fetchAnalytics();
  }, []);

  // Mock data for distribution since backend doesn't provide it yet
  const personalityDistribution = [
    { name: "Deep Soul", value: 15 },
    { name: "Explorer", value: 12 },
    { name: "Go-Getter", value: 20 },
    { name: "Nurturer", value: 18 },
    { name: "Socialite", value: 25 },
    { name: "Visionary", value: 10 },
  ];

  const genderDistribution = [
    { name: "Female", value: 65, color: "#EC4899" },
    { name: "Male", value: 30, color: "#3B82F6" },
    { name: "Other", value: 5, color: "#9CA3AF" },
  ];

  if (loading) return <div className="p-8 text-center text-gray-500">Loading analytics...</div>;

  return (
    <div className="space-y-8 p-6 bg-gray-50 min-h-screen">
      <div>
        <h2 className="text-3xl font-bold text-gray-800">Advanced Analytics</h2>
        <p className="text-gray-500">In-depth insights into user demographics and engagement</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
         {/* Summary Mini Cards */}
         {[
           { title: "Retention Rate", value: "84%", icon: <TrendingUp className="text-green-600" />, bg: "bg-green-50" },
           { title: "Avg. Daily Activity", value: "1.2h", icon: <Smile className="text-blue-600" />, bg: "bg-blue-50" },
           { title: "Matches Made", value: "1,240", icon: <Users className="text-purple-600" />, bg: "bg-purple-50" },
           { title: "Top Region", value: "NYC", icon: <MapPin className="text-orange-600" />, bg: "bg-orange-50" },
         ].map((item, i) => (
           <div key={i} className={`${item.bg} p-6 rounded-2xl shadow-sm border border-white flex items-center justify-between`}>
             <div>
               <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{item.title}</p>
               <p className="text-2xl font-bold text-gray-800">{item.value}</p>
             </div>
             <div className="p-3 bg-white/50 rounded-xl shadow-inner">{item.icon}</div>
           </div>
         ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Personality Distribution */}
        <div className="bg-white p-6 rounded-2xl shadow-md">
          <h3 className="text-lg font-bold text-gray-800 mb-6">Personality Distribution</h3>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={personalityDistribution}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {personalityDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" height={36}/>
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Gender Distribution */}
        <div className="bg-white p-6 rounded-2xl shadow-md">
          <h3 className="text-lg font-bold text-gray-800 mb-6">Gender Demographics</h3>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={genderDistribution}
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
                dataKey="value"
              >
                {genderDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" height={36}/>
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* User Activity Over Time */}
        <div className="bg-white p-6 rounded-2xl shadow-md lg:col-span-2">
           <h3 className="text-lg font-bold text-gray-800 mb-6">Engagement Overview</h3>
           <ResponsiveContainer width="100%" height={300}>
             <BarChart data={stats?.traffic || []}>
               <CartesianGrid strokeDasharray="3 3" vertical={false} />
               <XAxis dataKey="day" axisLine={false} tickLine={false} />
               <YAxis axisLine={false} tickLine={false} />
               <Tooltip cursor={{fill: '#f3b183', opacity: 0.1}}/>
               <Bar dataKey="users" fill="#db8654" radius={[4, 4, 0, 0]} barSize={40} />
             </BarChart>
           </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
